import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Tabs, Tab } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import RecipeService from "services/RecipeService";
import BackDropLoader from "components/common/BackDropLoader";
import Lottie from "react-lottie";
import { SHARE_LOTTIE_OPTIONS, SHARE_TYPES } from "configurations/Constants";
import {
  setSharedRecipeInfo,
  setShareRecipeSuccess,
} from "reduxStore/slices/recipeSlice";
import { useDispatch } from "react-redux";
import UserService from "services/UserService";
import {
  USER_DATA_FETCH_ERROR,
  PLEASE_SELECT_AT_LEAST_ONE_RECIPE,
  PLEASE_SEARCH_AND_SELECT_FARMER,
  PLEASE_SELECT_FARMER,
  RECIPE_SHARE_ERROR,
  LABEL_ENTER_MOBILE_NUMBER,
  LABEL_SELECT_RECIPES_TO_SHARE,
} from "configurations/MessageConstants";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { StatusCodes } from "configurations/StatusCodes";
import SharedRecipeDetails from "./SharedRecipeDetails";

const ShareRecipes = ({ type, recipes, showShareRecipe, onClose }) => {
  const dispatch = useDispatch();
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [farmerName, setFarmerName] = useState("");
  const [farmerEmail, setFarmerEmail] = useState("");
  const [activeTab, setActiveTab] = useState("share");
  const [isSearching, setIsSearching] = useState(false);

  const allRecipes = useSelector((state) => state.recipe.recipes);

  const defaultValues = {
    selectedRecipes: type === SHARE_TYPES.SINGLE ? [recipes[0]] : [],
    mobileNumber: "",
    farmerSelected: false,
    farmerEmail: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm({ defaultValues, mode: "onChange" });

  const watchedValues = watch();

  useEffect(() => {
    if (type === SHARE_TYPES.SINGLE && recipes[0]) {
      setValue("selectedRecipes", [recipes[0]]);
    } else {
      setValue("selectedRecipes", []);
    }
  }, [recipes, type, setValue]);

  const handleSearch = async () => {
    const mobileNumber = watchedValues.mobileNumber;
    if (!mobileNumber) return;
    setIsSearching(true);
    try {
      const userData = await UserService.getUserFromMobileNumber(mobileNumber);
      if (userData) {
        setShowCheckbox(true);
        setFarmerEmail(userData.email);
        setFarmerName(userData.name);
      }
    } catch (error) {
      if (error.response.status === StatusCodes.NOT_FOUND) {
        toast.error(error.response.data.detail);
      }
      console.error(USER_DATA_FETCH_ERROR, error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setShowCheckbox(false);
    setFarmerName("");
    setFarmerEmail("");
    setActiveTab("share");
    reset(defaultValues);
    onClose();
  };

  const handleClearCheckbox = () => {
    setValue("farmerSelected", false);
    setValue("farmerEmail", "");
    setFarmerName("");
    setFarmerEmail("");
    setShowCheckbox(false);
  };

  const handleRecipeSelect = (recipe) => {
    const currentSelected = watchedValues.selectedRecipes || [];
    const isSelected = currentSelected.some(
      (selectedRecipe) => selectedRecipe.id === recipe.id
    );
    const newSelectedRecipes = isSelected
      ? currentSelected.filter(
          (selectedRecipe) => selectedRecipe.id !== recipe.id
        )
      : [...currentSelected, recipe];
    setValue("selectedRecipes", newSelectedRecipes, {
      shouldValidate: isSubmitted,
    });
  };

  const onSubmit = async (data) => {
    if (!data.selectedRecipes?.length || !data.farmerEmail) {
      return;
    }

    try {
      const payload = {
        userEmail: data.farmerEmail,
        shareRecipes: data.selectedRecipes.map((recipe) => ({
          recipeId: recipe.id,
          recipeName: recipe.name,
          editor: true,
        })),
      };
      const response = await RecipeService.shareRecipes(payload);
      handleShare(response, farmerName);
    } catch (error) {
      console.error(RECIPE_SHARE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleShare = (response, farmerName) => {
    const groupedDetails = response.details.reduce(
      (group, { reason, recipeName }) => {
        if (!group[reason]) {
          group[reason] = { reason, recipeNames: [] };
        }
        group[reason].recipeNames.push(recipeName);
        return group;
      },
      {}
    );
    const details = Object.values(groupedDetails);
    dispatch(
      setSharedRecipeInfo({
        success: response.success,
        details,
        farmerName,
      })
    );
    dispatch(setShareRecipeSuccess(true));
    handleClose();
  };

  const renderShareContent = () => (
    <>
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie options={SHARE_LOTTIE_OPTIONS} height={100} width={100} />
          }
        />
      )}
      <Controller
        name="selectedRecipes"
        control={control}
        rules={{
          validate: (value) =>
            value && value.length > 0
              ? true
              : PLEASE_SELECT_AT_LEAST_ONE_RECIPE,
        }}
        render={() => null}
      />
      <div>
        {errors.selectedRecipes ? (
          <div className="validation-error">
            {errors.selectedRecipes.message}
          </div>
        ) : (
          <small>{LABEL_SELECT_RECIPES_TO_SHARE}</small>
        )}
      </div>
      <div className="mb-3 share-container">
        {(type === SHARE_TYPES.SINGLE ? recipes : allRecipes)?.map((recipe) => (
          <div
            key={recipe.id}
            className={`p-2 rounded border d-flex justify-content-between my-2 ${
              !recipe.isShared
                ? "my-recipes-share-card"
                : "shared-recipes-share-card"
            }`}
          >
            <div className="d-flex align-items-start text-truncate">
              <Form.Check
                type="checkbox"
                aria-label="Select Recipe"
                checked={(watchedValues.selectedRecipes || []).some(
                  (selectedRecipe) => selectedRecipe.id === recipe.id
                )}
                onChange={() => handleRecipeSelect(recipe)}
                className="me-2"
              />
              <div className="text-truncate">
                <div className="text-truncate">{recipe.name}</div>
                <small className="text-muted">{recipe.type}</small>
              </div>
            </div>
            <div className="ms-2">
              <small className="text-muted text-nowrap">
                {recipe.water} G/A
              </small>
            </div>
          </div>
        ))}
      </div>
      <Form.Group className="mb-3">
        <Form.Label>Mobile Number</Form.Label>
        <div className="d-flex gap-2">
          <Controller
            name="mobileNumber"
            control={control}
            rules={{
              required: "Mobile number is required",
              validate: (value) =>
                value.length === 10 || "Mobile number must be 10 digits",
            }}
            render={({ field }) => (
              <div className="w-100">
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={LABEL_ENTER_MOBILE_NUMBER}
                  maxLength={10}
                  className="input-underline"
                  isInvalid={!!errors.mobileNumber}
                  onChange={(e) => {
                    const number = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    field.onChange(number);
                    handleClearCheckbox();
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobileNumber?.message}
                </Form.Control.Feedback>
              </div>
            )}
          />
          <div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleSearch}
              aria-label="Search Mobile Number"
              disabled={
                isSearching ||
                errors.mobileNumber ||
                !watchedValues.mobileNumber
              }
            >
              {isSearching ? (
                <Spinner
                  animation="border"
                  size="sm"
                  className="align-middle"
                />
              ) : (
                <i className="fa fa-search" aria-hidden="true"></i>
              )}
            </Button>
          </div>
        </div>
      </Form.Group>
      {showCheckbox && (
        <Form.Group className="mb-3">
          <Controller
            name="farmerSelected"
            control={control}
            rules={{ required: PLEASE_SELECT_FARMER }}
            render={({ field }) => (
              <Form.Check
                {...field}
                type="checkbox"
                aria-label="Select Farmer"
                label={farmerName}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  setValue("farmerEmail", e.target.checked ? farmerEmail : "");
                }}
                isInvalid={!!errors.farmerSelected}
              />
            )}
          />
        </Form.Group>
      )}
    </>
  );

  return (
    <Modal
      show={showShareRecipe}
      onHide={handleClose}
      centered
      size="md"
      aria-labelledby="share-recipes-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title id="share-recipes-popup">
          {type === SHARE_TYPES.SINGLE ? "Share Recipe" : "Share Recipes"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {type === SHARE_TYPES.SINGLE ? (
            <Tabs
              id="recipe-tabs"
              activeKey={activeTab}
              onSelect={setActiveTab}
              className="mb-3"
            >
              <Tab eventKey="share" title="Share">
                {renderShareContent()}
              </Tab>
              <Tab eventKey="details" title="Details">
                <SharedRecipeDetails recipe={recipes[0]} />
              </Tab>
            </Tabs>
          ) : (
            renderShareContent()
          )}
        </Modal.Body>
        {activeTab === "share" && (
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Share
            </Button>
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default ShareRecipes;
